import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '@/assets/logo.svg';

const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const { mode, toggleMode } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { href: '#social', label: t.nav.social },
    { href: '#home', label: t.nav.home },
    { href: '#gallery', label: t.nav.gallery },
    { href: '#about', label: t.nav.about },
    { href: '#process', label: t.nav.process },
    { href: '#faq', label: t.nav.faq },
    { href: '#contact', label: t.nav.contact },
  ];

  const scrollToEl = (href: string) => {
    const navEl = document.querySelector('nav');
    const offset = (navEl?.getBoundingClientRect().height ?? 64) + 8;
    const el = document.querySelector(href) as HTMLElement | null;
    if (!el) return false;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
    // dorovnání po donačtení lazy sekcí
    window.setTimeout(() => {
      const again = document.querySelector(href) as HTMLElement | null;
      if (!again) return;
      const target = again.getBoundingClientRect().top + window.scrollY - offset;
      if (Math.abs(target - window.scrollY) > 8) {
        window.scrollTo({ top: target, behavior: 'smooth' });
      }
    }, 900);
    return true;
  };

  const scrollTo = (href: string) => {
    setMobileOpen(false);
    const start = Date.now();
    const attempt = () => {
      if (scrollToEl(href)) return;
      if (Date.now() - start < 2000) {
        window.setTimeout(attempt, 100);
      } else {
        window.location.hash = href;
      }
    };
    // počkat na dokončení animace zavírání menu
    window.setTimeout(attempt, 320);
  };


  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background/90 backdrop-blur-xl shadow-lg shadow-black/30 border-b border-primary/10' : 'bg-gradient-to-b from-black/40 to-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <button onClick={() => scrollTo('#home')} className="flex items-center gap-2">
            <motion.img
              src={logo}
              alt="Tom Art Mask"
              className={`h-10 md:h-12 w-auto transition-all duration-500 ${scrolled ? 'drop-shadow-[0_0_8px_hsl(var(--primary)/0.4)]' : ''}`}
            />
          </button>

          {/* Desktop */}
          <div className="hidden md:flex items-center gap-6">
            {links.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href)}
                className="nav-link-underline text-xs uppercase tracking-widest text-foreground/90 hover:text-primary transition-colors"
                style={{ textShadow: '0 1px 8px rgba(0,0,0,0.6)' }}
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={toggleMode}
              className="p-2 rounded-md border border-border text-foreground/80 hover:text-primary hover:border-primary transition-colors"
              title={mode === 'dark' ? 'Light mode' : 'Dark mode'}
            >
              {mode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="flex gap-1 ml-1 border border-border rounded-md overflow-hidden">
              <button
                onClick={() => setLang('cs')}
                className={`px-2 py-1 text-xs font-bold transition-colors ${lang === 'cs' ? 'bg-primary text-primary-foreground' : 'text-foreground/70 hover:text-foreground'}`}
              >
                CZ
              </button>
              <button
                onClick={() => setLang('en')}
                className={`px-2 py-1 text-xs font-bold transition-colors ${lang === 'en' ? 'bg-primary text-primary-foreground' : 'text-foreground/70 hover:text-foreground'}`}
              >
                EN
              </button>
            </div>
          </div>

          {/* Mobile toggle */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={toggleMode}
              className="p-1.5 rounded-md border border-border text-foreground/80 hover:text-primary transition-colors"
            >
              {mode === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            <div className="flex gap-1 border border-border rounded-md overflow-hidden">
              <button
                onClick={() => setLang('cs')}
                className={`px-2 py-1 text-xs font-bold transition-colors ${lang === 'cs' ? 'bg-primary text-primary-foreground' : 'text-foreground/70'}`}
              >
                CZ
              </button>
              <button
                onClick={() => setLang('en')}
                className={`px-2 py-1 text-xs font-bold transition-colors ${lang === 'en' ? 'bg-primary text-primary-foreground' : 'text-foreground/70'}`}
              >
                EN
              </button>
            </div>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="text-foreground"
              aria-label={mobileOpen ? 'Zavřít menu' : 'Otevřít menu'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="md:hidden overflow-hidden bg-background/98 backdrop-blur-md border-t border-border"
          >
            <div className="px-4 py-4 space-y-1">
              {links.map((link, i) => (
                <motion.button
                  key={link.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => scrollTo(link.href)}
                  className="block w-full text-left text-sm uppercase tracking-widest text-foreground/90 hover:text-primary transition-colors py-2"
                >
                  {link.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
